import React from 'react'
import { Redirect, Route, RouteProps } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { ApplicationState } from 'src/store'
import UpdateTermsAndPrivacyPolicyModal from 'src/views/update-terms-and-privacy-policy-modal'
import UserDataLoader from 'src/components/UserDataLoader'

function pathCheck(path: string | string[], urls: Array<string | RegExp>) {
  if (typeof path === 'string') {
    return urls.some(url => path.search(url) > -1)
  }
  return path.some(nextPath => urls.some(url => nextPath.search(url) > -1))
}

function ProtectedRoute({
  component: Component,
  exact,
  location,
  path,
  render
}: RouteProps) {
  const {
    auth,
    me: { loading, organization, user }
  } = useSelector((state: ApplicationState) => ({
    auth: state.auth,
    me: state.me
  }))

  if (!auth.access_token || !auth.refresh_token) {
    return <Redirect to="/403" />
  }

  const isLoading =
    !user || loading.user || !organization || loading.organization
  if (isLoading) {
    return <UserDataLoader />
  }

  if (!user?.agree_terms) {
    return <UpdateTermsAndPrivacyPolicyModal />
  }

  const routes = [/^\/$/, '/403', '/404', '/help']

  const isAgency = organization?.tenant?.tenant_type === 'AGENCY'
  if (isAgency) {
    /** isAgency
     * path="/agencies/issuances"
     * path="/agencies/issuances/:request_agency_id/:request_id/:organization_id/summary"
     */
    routes.push('/agencies')
    const checkHasPermission = pathCheck(path as string, routes)
    if (!checkHasPermission) {
      return <Redirect to="/403" />
    }
  }

  /**
   * Basic routes
   */
  routes.push('/my-requests')
  Object.entries(user?.role_group).forEach(([key, value]) => {
    /**
     * path="/approvals"
     * path="/approvals/:request_approval_id/request/:request_id"
     * path="/audits"
     * path="/audits/:request_id/summary"
     * path="/closings"
     * path="/closings/:request_closing_id/request/:request_id"
     * path="/conferences"
     * path="/conferences/:request_conference_id/request/:request_id"
     *
     */
    if (value.length) {
      switch (key) {
        case 'approver':
          routes.push('/approvals')
          break
        case 'checker':
          routes.push('/conferences')
          break
        case 'controller':
          routes.push('/closings')
          break
        case 'manager':
          routes.push('/management/my-team')
          routes.push('/management/reports')
          break
        default:
          break
      }
    }
  })
  const isAdmin = ['ADMIN', 'REALM_ADMIN', 'SUPER_ADMIN'].includes(
    user?.user_level_type
  )

  if (isAdmin) {
    /**
     * path="/management/administration/antifraud
     * path="/management/administration/email/notifications
     * path="/management/administration/resources/:resource"
     * path="/management/tree"
     * path="/management/flow"
     * path="/policies"
     * path="/policies/create"
     * path="/policies/edit/:policy_id"
     * path="/users"
     * path="/users/create"
     * path="/users/edit/:user_id"
     */
    routes.push('/management/administration')
    routes.push('/management/tree')
    routes.push('/management/flow')
    routes.push('/policies')
    routes.push('/users')
  }

  const canCreateTenant = [597990758089].includes(+user.tenant_id)

  if (canCreateTenant) {
    /**
     * path="/tenant"
     * path="/tenant/:tenant_id"
     */
    routes.push('/tenant')
  }

  if (isAdmin || user.dashboard || user.role_group.manager.length > 0) {
    routes.push('/dashboard')
  }

  if (user.auditor) {
    /**
     * path="/audits"
     * path="/audits/:request_id/summary"
     */
    routes.push('/audits')
  }

  if ([597990758089, 299595062723, 559042084949].includes(+user.tenant_id)) {
    routes.push('/request-classification')
  }

  if (user?.in_toodopay) {
    /**
     * path="/expense-classification"
     */
    routes.push('/expense-classification')
  }

  const checkHasPermission = pathCheck(path as string, routes)

  if (!checkHasPermission) {
    return <Redirect to="/403" />
  }

  return (
    <Route
      component={Component}
      // computedMatch={computedMatch}
      exact={exact}
      location={location}
      path={path}
      render={render}
    />
  )
}
export default ProtectedRoute
